/**
 * TanStack Query hooks for the SubContractorHub Adder Mapping tab (SCH-2.3).
 *
 * Mirrors useStageMappings (SCH-2.2) and useFieldMappings (SCH-2.1)
 * including their post-deploy follow-up fixes:
 *   - Direct cache splice on single-row mutations (no full refetch).
 *   - 5-min staleTime + refetchOnWindowFocus on the parent query.
 *   - Per-row pending state via mutation.variables.
 *
 * Architectural difference from SCH-2.2: there is NO separate listing
 * endpoint cache. The discover-adders payload already includes
 * `existingMappings`, so all CRUD splices target the discover-adders
 * cache directly. This avoids a second round-trip when the tab opens —
 * everything the FE needs (discovered names + suggestions + existing
 * mappings + candidate catalog) arrives in one envelope.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { adderMappingService } from '@/services/integrations/adderMappingService';
import type {
  AdderMappingRow,
  DiscoverAddersResponse,
} from '@/types/integrations/adderMapping';

const FIVE_MINUTES = 5 * 60 * 1000;

function discoverAddersQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'discover-adders'] as const;
}

export function useDiscoverAdders() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: discoverAddersQueryKey(tenantId),
    queryFn: async () => {
      const result = await adderMappingService.discoverAdders();
      if (result.error) {
        const err = new Error(result.error.message) as Error & {
          code?: string;
          details?: Record<string, unknown>;
        };
        err.code = result.error.code;
        err.details = result.error.details;
        throw err;
      }
      return result.data;
    },
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
  });
}

/** Splice helper: replace-or-append a mapping row by externalAdderName.
 *
 *  UPSERT semantics: if a row with the same externalAdderName exists,
 *  replace it; otherwise append. Mirrors useStageMappings' approach but
 *  splices into the nested existingMappings array of the discover-adders
 *  cache, since there's no separate list query for adders.
 */
function _spliceMapping(
  prev: DiscoverAddersResponse | undefined,
  newRow: AdderMappingRow,
): DiscoverAddersResponse | undefined {
  if (!prev) return prev;
  const filtered = prev.existingMappings.filter(
    (r) => r.externalAdderName !== newRow.externalAdderName,
  );
  return { ...prev, existingMappings: [...filtered, newRow] };
}

export function useCreateAdderMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverAddersQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: adderMappingService.createAdderMapping,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const newRow = result.data;
      queryClient.setQueryData<DiscoverAddersResponse>(queryKey, (prev) =>
        _spliceMapping(prev, newRow),
      );
    },
  });
}

/** Atomic create-adder + map: splices BOTH the new catalog candidate
 *  and the new mapping row into the discover-adders cache so the FE
 *  stays consistent without a refetch.
 */
export function useCreateAdderWithMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverAddersQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: adderMappingService.createAdderWithMapping,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const { adder, mapping } = result.data;
      queryClient.setQueryData<DiscoverAddersResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        // Append the new candidate to joulesAdders (deduplicate by id in case
        // a parallel discover already pulled it in — defensive).
        const candidates = prev.joulesAdders.some((c) => c.id === adder.id)
          ? prev.joulesAdders
          : [
              ...prev.joulesAdders,
              {
                id: adder.id,
                name: adder.name,
                category: adder.category,
                pricingModel: adder.pricingModel,
                unitPrice: adder.unitPrice,
                unitLabel: adder.unitLabel,
              },
            ];
        // Replace-or-append the mapping row (UPSERT semantics).
        const filtered = prev.existingMappings.filter(
          (r) => r.externalAdderName !== mapping.externalAdderName,
        );
        return {
          ...prev,
          joulesAdders: candidates,
          existingMappings: [...filtered, mapping],
        };
      });
    },
  });
}

export function useUpdateAdderMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverAddersQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof adderMappingService.updateAdderMapping>[1];
    }) => adderMappingService.updateAdderMapping(id, body),
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const updated = result.data;
      queryClient.setQueryData<DiscoverAddersResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          existingMappings: prev.existingMappings.map((r) =>
            r.id === updated.id ? updated : r,
          ),
        };
      });
    },
  });
}

export function useDeleteAdderMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverAddersQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: adderMappingService.deleteAdderMapping,
    onSuccess: (result, deletedId) => {
      if (result.error) return;
      queryClient.setQueryData<DiscoverAddersResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          existingMappings: prev.existingMappings.filter((r) => r.id !== deletedId),
        };
      });
    },
  });
}

export { discoverAddersQueryKey };
